import { documentSchemaStore } from "@src/lib/schemas";
import primitiveFieldsJsonSchema from "../../schemas/primitive_fields.json";
import { templateObjectsService } from "@src/lib/schemas/TemplateObjectsService";
import { JSONSchema9, JSONSchema9TypeName } from "@src/lib/types/jsonSchema";
import { FeatureFlagManager } from "@src/taskpane/hooks/useFeatureFlags";

export const TYPE_OPTIONS_CHANGED_EVENT = "walter-type-options-changed";

export type TypeOption = {
  label: string;
  ref?: string;
  value: JSONSchema9TypeName;
  featureFlags?: string[];
};

export const REF_PREFIX = "#/definitions/";

class TypeOptionsHandler {
  #typeOptions: TypeOption[];

  constructor() {
    this.#typeOptions = this.buildTypeOptions();
  }

  /**
   * Rebuild type options (useful when schema changes)
   */
  refresh() {
    this.#typeOptions = this.buildTypeOptions();
  }

  /**
   * Lookup a definition by its URI (e.g., "#/definitions/stakeholder").
   *
   * This looks in two places (in this order):
   * 1. The current document schema
   * 2. The template_objects.json file
   *
   * If the definition is not found in either place, undefined is returned.
   *
   * @param definitionUri
   * @returns
   */
  deref(definitionUri: string): JSONSchema9 | undefined {
    return deref(definitionUri);
  }

  get typeOptions() {
    const res = this.#typeOptions.map(async (option) => {
      if (option.featureFlags === undefined) {
        return true;
      } else {
        return await FeatureFlagManager.getInstance().areEnabled(option.featureFlags);
      }
    });

    return (async () =>
      (await Promise.all(res)).reduce((allowedOptions, allowed, index) => {
        if (allowed) {
          allowedOptions.push(this.#typeOptions[index]);
        }

        return allowedOptions;
      }, [] as TypeOption[]))();
  }

  private buildTypeOptions() {
    const templateObjectsSchema = templateObjectsService.getSchemaSync();
    if (!templateObjectsSchema) {
      return [];
    }

    const templateObjectTypes = Object.entries(templateObjectsSchema.definitions || {}).map(([key, objectSchema]) => {
      // Skip boolean schemas (they don't have titles)
      if (typeof objectSchema === "boolean") {
        return {
          label: key,
          value: key,
          ref: `#/definitions/${key}`,
        } as TypeOption;
      }

      return {
        label: objectSchema.title || key,
        value: key,
        ref: `#/definitions/${key}`,
        // Only include feature flags if they exist
        ...("feature_flags" in objectSchema && { featureFlags: objectSchema.feature_flags }),
      } as TypeOption;
    });

    const primitiveTypes = Object.values(primitiveFieldsJsonSchema.definitions).map((schema) => {
      return {
        label: schema.title,
        value: schema.type,
      } as TypeOption;
    });

    return templateObjectTypes.concat(primitiveTypes).sort((a, b) => a.label.localeCompare(b.label));
  }
}

/**
 * Lookup a definition by its URI (e.g., "#/definitions/stakeholder").
 *
 * This looks in three places (in this order):
 * 1. The provided definitions (via the `additionalDefinitions` parameter)
 * 2. The current document schema
 * 3. The template_objects.json file
 *
 * If the definition is not found in either place, undefined is returned.
 */
export function deref(
  definitionUri: string,
  additionalDefinitions?: JSONSchema9["definitions"],
): JSONSchema9 | undefined {
  if (!definitionUri.startsWith(REF_PREFIX)) return;

  const definitionName = nameFromRef(definitionUri);
  const templateObjectsSchema = templateObjectsService.getSchemaSync();
  const definition =
    additionalDefinitions?.[definitionName] ??
    documentSchemaStore.schema.definitions?.[definitionName] ??
    templateObjectsSchema?.definitions?.[definitionName];

  if (typeof definition === "boolean") {
    // If the definition is a boolean, it validates any JSON. Because we expect it to be an object, we return an empty object (which also validates for any JSON).
    return {};
  } else {
    return definition;
  }
}

/**
 * Extract the name from a definition URI (e.g., "#/definitions/stakeholder" -> "stakeholder").
 */
export function nameFromRef(definitionUri: string): string {
  return definitionUri.replace(REF_PREFIX, "");
}

export const typeOptionsHandler = new TypeOptionsHandler();
export type { TypeOptionsHandler };
